import {
    ISimplePresentation,
    ISimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {KennethMustDie} from "../KennethMustDie";
import {IBetWayWinData, KMD_SYMBOL_ANIMATIONS, SPECIAL_SYMBOL_NAMES, X_NUDGE_ANIMATIONS} from "../server/IKMDData";
import {SimpleReelModel, SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {KMDSymbol} from "../reels/KMDSymbol";
import {gsap} from "gsap";
import {KMDSound} from "../sound/KMDSound";

export class KMDNormalBetWaysWinRunner implements ISimplePresentation {

    protected _canPlayWinSounds: boolean;

    public shouldRun(): boolean {
        return (KennethMustDie.model.gameData.betWayWins.length > 0);
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const betWayWins: IBetWayWinData[] = KennethMustDie.model.gameData.betWayWins,
            tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.1,
                onComplete: () => {
                    controller.finish();
                }
            }),
            allWinPositions: boolean[][] = this.getAllWinPositions(betWayWins),
            m1M2SymbolLines: number[] = KennethMustDie.model.gameData.symbolLines;

        this._canPlayWinSounds = !(KennethMustDie.isSingleWinBelowStake());

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        this.animateNoWin(allWinPositions, tl);
        this.animatePreWin(betWayWins, tl);
        this.animateWin(allWinPositions, tl);

        // #343
        tl.addLabel("showWinAmountNWays");
        if (m1M2SymbolLines && m1M2SymbolLines.length) {
            this.showWinAmountNWays(betWayWins, tl, "showWinAmountNWays");
        }
    }

    protected getAllWinPositions(betWayWins: IBetWayWinData[]): boolean[][] {
        const allWinPositions: boolean[][] = [
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false]
        ];

        betWayWins.forEach((betWayWin: IBetWayWinData) => {
            betWayWin.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val) {
                        allWinPositions[reelId][symId] = val;
                    }
                });
            });
        });

        return allWinPositions;
    }

    protected animateNoWin(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        // hiding the locks
        KennethMustDie.stickyWilds.hideAllStickyWilds();
        KennethMustDie.stickyXNudges.hideAllStickyXNudges();

        tl.add(() => {
            allWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (!val) {
                        const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                            effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                            symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId);

                        if (symbol) {
                            const sym: KMDSymbol = symbol.positionable as KMDSymbol;

                            if (sym.getExtrasLength()) {
                                KennethMustDie.xWaysReveal.addNoWinTint(reelId);
                            }

                            sym.getNoWinStateAnimation();
                        }
                    }
                });
            });
        });
    }

    protected animatePreWin(betWayWins: IBetWayWinData[], tl: gsap.core.Timeline): void {
        betWayWins.forEach((betWayWin: IBetWayWinData) => {
            const timeLine: gsap.core.Timeline = gsap.timeline();

            // #411
            if (this._canPlayWinSounds) {
                timeLine.add(() => {
                    KennethMustDie.soundPlayer.playSound(KMDSound.WIN_BLINK);
                }, 0);
            }

            betWayWin.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val) {
                        const symbolAnim: gsap.core.Timeline = gsap.timeline(),
                            reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                            effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                            symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                            sym: KMDSymbol = symbol.positionable as KMDSymbol,
                            multi: number = KennethMustDie.model.gameData.multiplier[reelId][symId];

                        symbolAnim.add(sym.getPreWinStateAnimation());
                        symbolAnim.add(sym.displayMultiOnWin(multi), "<");

                        timeLine.add(symbolAnim, 0);
                    }
                });
            });

            timeLine.to({}, {
                duration: 0.2
            });
            tl.add(timeLine);
        });
    }

    protected animateWin(allWinPositions: boolean[][], tl: gsap.core.Timeline): void {
        const timeLine: gsap.core.Timeline = gsap.timeline();

        // #411
        if (this._canPlayWinSounds) {
            timeLine.add(() => {
                KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SCRAMBLE);
            }, 0);
        }

        allWinPositions.forEach((value: boolean[], reelId: number) => {
            value.forEach((val: boolean, symId: number) => {
                if (val) {
                    const reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                        effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                        symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                        sym: KMDSymbol = symbol.positionable as KMDSymbol;

                    if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                        let winState: string = ((reel.id === KennethMustDie.REELS_NUM && symbol.name === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) ? X_NUDGE_ANIMATIONS.FULL_WIN_3 : KMD_SYMBOL_ANIMATIONS.WIN);

                        if (symId === 2) {
                            winState = X_NUDGE_ANIMATIONS.FULL_WIN_2;
                        }

                        timeLine.add(sym.setState(winState), 0);
                    } else if (KennethMustDie.model.gameData.evaluatedArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) {
                        timeLine.add(sym.playOverlayAnimation("flicker"), 0);
                        timeLine.add(sym.setInitialWinState(), 0);
                    }
                }
            });
        });

        tl.add(timeLine);
    }

    // #343
    protected showWinAmountNWays(betWayWins: IBetWayWinData[], tl: gsap.core.Timeline, label: string): void {
        const timeLine: gsap.core.Timeline = gsap.timeline();

        // #507
        timeLine.add(() => {
            KennethMustDie.soundPlayer.playSound(KMDSound.WAYS_WON_PRESENTATION);
        });

        betWayWins.forEach((betWay: IBetWayWinData, idx: number) => {
            timeLine.add(() => {
                KennethMustDie.winAmount.showWinAmountNWays(betWay);
            }, (idx * 0.3));
        });

        tl.add(timeLine, label);
    }

    protected onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
    }
}